import React, { useEffect, useState, useCallback } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Building2, MapPin, Mail, Phone, Clock, ShieldAlert, CreditCard, Circle, ExternalLink, Users } from "lucide-react";
import AgencyDemoPortal from "@/pages/agency/AgencyDemoPortal";

const two = (n) => String(n).padStart(2, "0");
const fmtClock = (s) => `${two(Math.floor(s / 60))}:${two(s % 60)}`;

function AgencyPayments() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/agency/connect/status"); setSt(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/agency/connect/onboard"); if (data.url) window.location.href = data.url; }
    catch { toast.error("Could not start Stripe setup."); setBusy(false); }
  };

  const kyc = st?.kyc_status || "not_started";
  const active = kyc === "active";
  const badge = {
    active: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Payments active"],
    pending: ["bg-amber-50 text-amber-700 border-amber-200", "Verification in progress"],
    restricted: ["bg-red-50 text-red-700 border-red-200", "Action needed"],
    not_started: ["bg-slate-50 text-slate-600 border-slate-200", "Not connected"],
  }[kyc] || ["bg-slate-50 text-slate-600 border-slate-200", kyc];
  const fee = st?.fee_config || {};
  const feeLabel = fee.mode === "monthly"
    ? `Flat $${Number(fee.monthly_amount || 0).toFixed(2)}/mo`
    : `${Number(fee.percent ?? 15).toFixed(0)}% platform fee per payment`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="agency-payments-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Payments</p>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${badge[0]}`} data-testid="agency-kyc-status">
          <CreditCard className="h-3.5 w-3.5" /> {badge[1]}
        </span>
      </div>
      <p className="mt-3 text-sm text-slate-600">
        {active
          ? "Your Stripe account is connected. Client payments settle directly into your account, and ARPS's platform fee is collected automatically on each payment."
          : kyc === "pending"
            ? "Your details are with Stripe for verification — this usually takes a few minutes, occasionally up to a day. You'll move from Pending to Active automatically once Stripe finishes; until then, client payments are held with ARPS and not yet routed to you."
            : kyc === "restricted"
              ? "Stripe needs a little more information before your account can receive payments. Continue setup to resolve the outstanding items."
              : "Connect your business's Stripe account so your clients' payments settle directly into your account. Full business verification (KYC) is completed securely on Stripe and usually takes just a few minutes."}
      </p>
      <p className="mt-2 text-xs font-medium text-slate-500" data-testid="agency-fee-label">Your plan: {feeLabel}</p>
      {!active && (
        <Button data-testid="agency-connect-stripe" onClick={connect} disabled={busy} className="mt-4 bg-[#635bff] text-white hover:bg-[#524dd6]">
          {busy ? "Opening Stripe…" : (st?.has_account ? "Continue Stripe setup" : "Connect Stripe account")}
        </Button>
      )}
    </div>
  );
}

function PayoutsBanner() {
  const [kyc, setKyc] = useState(null);
  useEffect(() => {
    let on = true;
    api.get("/agency/connect/status").then(({ data }) => { if (on) setKyc(data.kyc_status || "not_started"); }).catch(() => {});
    return () => { on = false; };
  }, []);
  if (!kyc || kyc === "active") return null;
  const msg = {
    not_started: "Connect your payouts to start getting paid. Until your Stripe account is connected and verified, your clients' payments are held with ARPS and not yet routed to your account.",
    pending: "Your Stripe verification is in progress. Client payments won't reach your account until Stripe marks you Active — this usually completes within minutes.",
    restricted: "Stripe needs more information before you can receive payments. Open Payments below to finish setup — until then, client payments are not routed to you.",
  }[kyc] || "Finish connecting your payouts so client payments route to your account.";
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="agency-payouts-banner">
      <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <p className="text-sm font-medium text-amber-800">{msg}</p>
    </div>
  );
}

function GettingStarted({ slug }) {
  const [kyc, setKyc] = useState(null);
  useEffect(() => {
    let on = true;
    api.get("/agency/connect/status").then(({ data }) => { if (on) setKyc(data.kyc_status || "not_started"); }).catch(() => {});
    return () => { on = false; };
  }, []);
  const steps = [
    { key: "stripe", label: "Connect your payouts", done: kyc === "active",
      hint: kyc === "active" ? "Stripe connected — client payments route to your account." : "Set this up in the Payments section below.", icon: CreditCard },
    { key: "site", label: "Share your intake site", done: false, hint: "Send clients your branded request page to start taking work.",
      href: slug ? `/a/${slug}` : null, cta: "View site", icon: ExternalLink },
    { key: "staff", label: "Add your team", done: false, hint: "Ask your ARPS contact to add staff accounts to your agency.",
      href: "/contact", cta: "Contact ARPS", icon: Users },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="agency-getting-started">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Getting started</p>
        <span className="text-xs font-medium text-slate-400">{doneCount} of {steps.length} done</span>
      </div>
      <ul className="mt-4 space-y-3">
        {steps.map((s) => (
          <li key={s.key} className="flex items-start gap-3" data-testid={`gs-step-${s.key}`}>
            {s.done
              ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" data-testid={`gs-done-${s.key}`} />
              : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${s.done ? "text-slate-500 line-through" : "text-slate-900"}`}>{s.label}</p>
              <p className="text-xs text-slate-500">{s.hint}</p>
            </div>
            {!s.done && s.href && (
              <a href={s.href} target="_blank" rel="noreferrer" data-testid={`gs-cta-${s.key}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                {s.cta} <s.icon className="h-3.5 w-3.5" />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MonthlyFeeCard() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/agency/billing/status"); setB(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!b || b.mode !== "monthly") return null;
  const status = b.billing_status || "not_started";
  const active = status === "active";
  const tone = {
    active: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Active"],
    past_due: ["bg-red-50 text-red-700 border-red-200", "Payment failed"],
    checkout_started: ["bg-amber-50 text-amber-700 border-amber-200", "Awaiting confirmation"],
    not_started: ["bg-amber-50 text-amber-700 border-amber-200", "Not set up"],
    canceled: ["bg-slate-50 text-slate-600 border-slate-200", "Canceled"],
  }[status] || ["bg-slate-50 text-slate-600 border-slate-200", status];
  const amt = Number(b.monthly_amount || 0).toFixed(2);
  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/agency/billing/start-subscription"); if (data.url) window.location.href = data.url; }
    catch (e) { toast.error(extractErrorMessage(e, "Could not start billing")); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="agency-monthly-billing">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Monthly platform fee</p>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone[0]}`} data-testid="agency-billing-status">
          <CreditCard className="h-3.5 w-3.5" /> {tone[1]}
        </span>
      </div>
      <p className="mt-3 text-sm text-slate-600">
        {active
          ? `Your flat platform fee of $${amt}/month is active and billed automatically.`
          : status === "past_due"
            ? "Your last monthly payment failed. Please update your card to keep your account in good standing."
            : `Your plan is a flat $${amt}/month platform fee. Set up automatic billing to activate it — you'll enter a card on Stripe and it renews monthly.`}
      </p>
      {!active && (
        <Button data-testid="agency-setup-billing" onClick={start} disabled={busy} className="mt-4 bg-[#635bff] text-white hover:bg-[#524dd6]">
          {busy ? "Opening…" : (status === "past_due" ? "Update payment method" : "Set up monthly billing")}
        </Button>
      )}
    </div>
  );
}

function LeadNotifications() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/agency/lead-recipients"); setData(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const recipients = data.recipients || [];
  if (recipients.length === 0) return null;

  const toggle = async (r) => {
    setBusy(r.id);
    try {
      const { data: res } = await api.put("/agency/lead-recipients", { user_id: r.id, enabled: !r.lead_emails });
      setData((d) => ({ ...d, recipients: res.recipients }));
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update recipient"));
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="agency-lead-notifications">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-slate-400" />
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Lead email notifications</p>
      </div>
      <p className="mt-2 text-sm text-slate-600">Choose who on your team is emailed when a new client lead lands on your intake page. At least one person must stay on.</p>
      <ul className="mt-4 divide-y divide-slate-100">
        {recipients.map((r) => (
          <li key={r.id} className="flex items-center justify-between py-2.5" data-testid={`lead-recipient-${r.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{r.name || r.email}{r.owner && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Owner</span>}</p>
              <p className="truncate text-xs text-slate-500">{r.email}</p>
            </div>
            <button
              type="button"
              disabled={!data.can_edit || busy === r.id}
              data-testid={`lead-recipient-toggle-${r.id}`}
              onClick={() => toggle(r)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${r.lead_emails ? "bg-emerald-500" : "bg-slate-300"} ${(!data.can_edit || busy === r.id) ? "opacity-60" : ""}`}
              aria-pressed={r.lead_emails}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${r.lead_emails ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </li>
        ))}
      </ul>
      {!data.can_edit && <p className="mt-2 text-xs text-slate-400">Only the agency administrator can change these.</p>}
    </div>
  );
}

export default function AgencyDashboard() {
  const [me, setMe] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [nudge, setNudge] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/agency/me"); setMe(data); if (data.is_demo) setRemaining(data.remaining_seconds); }
    catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  // Local countdown; re-sync with the server (authoritative) every 30s.
  useEffect(() => {
    if (!me?.is_demo || me?.expired) return undefined;
    const tick = setInterval(() => setRemaining((r) => (r == null ? r : Math.max(0, r - 1))), 1000);
    const sync = setInterval(load, 30000);
    return () => { clearInterval(tick); clearInterval(sync); };
  }, [me, load]);

  useEffect(() => { if (me?.is_demo && remaining === 0 && !me.expired) load(); }, [remaining, me, load]);

  const requestActivation = async () => {
    setRequesting(true);
    try { await api.post("/agency/request-activation"); toast.success("Activation request sent. Our team will contact you."); }
    catch { toast.error("Could not send request."); }
    finally { setRequesting(false); }
  };

  const expired = me?.is_demo && (me?.expired || remaining === 0);

  if (expired) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center" data-testid="agency-eval-ended">
        <ShieldAlert className="mx-auto h-14 w-14 text-amber-500" />
        <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Your ARPS Agency Evaluation Has Ended</h1>
        <p className="mt-3 text-sm text-slate-600">Thank you for exploring ARPS. Your 60-minute evaluation has ended and demo access is now closed.</p>
        <Button data-testid="request-agency-activation" onClick={requestActivation} disabled={requesting} className="mt-6 bg-sky-600 text-white hover:bg-sky-700">
          {requesting ? "Sending…" : "Request Agency Activation"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-dashboard">
      {me?.is_demo && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="demo-mode-banner">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-amber-500 px-2 py-0.5 text-xs font-extrabold uppercase tracking-widest text-white">Demo / Evaluation</span>
            <span className="text-xs font-medium text-amber-800">All clients, assignments, providers, documents & financials are simulated demonstration data.</span>
          </div>
          <div className="flex items-center gap-1.5 font-mono text-lg font-bold text-amber-800" data-testid="demo-timer">
            <Clock className="h-4 w-4" /> DEMO MODE — {remaining != null ? fmtClock(remaining) : "60:00"} REMAINING
          </div>
        </div>
      )}
      {me?.is_demo && remaining != null && remaining <= 600 && remaining > 0 && nudge && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3" data-testid="demo-convert-nudge">
          <p className="text-sm font-medium text-sky-800">Enjoying ARPS? Only {Math.ceil(remaining / 60)} min left in your evaluation — activate now to keep this workspace and your team.</p>
          <div className="flex items-center gap-3">
            <Button data-testid="nudge-request-activation" size="sm" onClick={requestActivation} disabled={requesting} className="bg-sky-600 text-white hover:bg-sky-700">
              {requesting ? "Sending…" : "Request Activation"}
            </Button>
            <button data-testid="nudge-dismiss" onClick={() => setNudge(false)} className="text-xs font-medium text-sky-500 hover:text-sky-700">Dismiss</button>
          </div>
        </div>
      )}
      {me?.is_demo && remaining != null && remaining <= 300 && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" data-testid="demo-expiry-warning">Your evaluation ends in under 5 minutes.</p>
      )}

      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{me?.agency_name || "Your Agency"}</h1>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1" data-testid="agency-status-badge">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-700">{me?.is_demo ? "Evaluation Active" : "Active Agency"}</span>
        </div>
      </div>

      {me?.is_demo ? (
        <AgencyDemoPortal />
      ) : (
        <>
          <PayoutsBanner />
          <GettingStarted slug={me?.slug} />
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Agency profile</p>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3"><Building2 className="mt-0.5 h-4 w-4 text-slate-400" /><div><dt className="text-xs text-slate-500">Administrator</dt><dd className="text-sm font-medium text-slate-900">{me?.contact_name || "—"}</dd></div></div>
              <div className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 text-slate-400" /><div><dt className="text-xs text-slate-500">Email</dt><dd className="text-sm font-medium text-slate-900">{me?.email || "—"}</dd></div></div>
              <div className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 text-slate-400" /><div><dt className="text-xs text-slate-500">Phone</dt><dd className="text-sm font-medium text-slate-900">{me?.phone || "—"}</dd></div></div>
              <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-slate-400" /><div><dt className="text-xs text-slate-500">Primary service area</dt><dd className="text-sm font-medium text-slate-900">{me?.primary_service_area || "—"}</dd></div></div>
            </dl>
          </div>
          <AgencyPayments />
          <MonthlyFeeCard />
          <LeadNotifications />
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Welcome to your ARPS agency environment. Assignment, server, billing, and payout tools will appear here as your platform is configured.</p>
          </div>
        </>
      )}

      {me?.is_demo && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">Ready to move beyond the evaluation? Convert this demonstration into a fully operational ARPS agency account.</p>
          <Button data-testid="request-agency-activation-inline" variant="outline" onClick={requestActivation} disabled={requesting} className="mt-4">
            {requesting ? "Sending…" : "Request Agency Activation"}
          </Button>
        </div>
      )}
    </div>
  );
}
